// Seeded RNG: mulberry32 with named sub-streams.
// Each stream's state lives inside GameState (`state.rng[name]`) so a save
// captures it and a loaded game continues with exactly the same random draws.

export interface RngStream {
  s: number; // uint32 state
}

/** FNV-1a hash of a string to uint32. Used to derive sub-stream seeds. */
export function hashString(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Mix two uint32 values into one (splitmix-style finalizer). */
export function mixSeed(a: number, b: number): number {
  let h = (a ^ Math.imul(b, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}

export class Rng {
  constructor(public readonly stream: RngStream) {}

  static fromSeed(seed: number): Rng {
    return new Rng({ s: seed >>> 0 });
  }

  /** Float in [0, 1). */
  next(): number {
    let t = (this.stream.s = (this.stream.s + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }

  /** Pick a key from a weight table. Weights need not sum to 1. */
  weighted<K extends string>(table: Readonly<Record<K, number>>): K {
    const keys = Object.keys(table) as K[];
    let total = 0;
    for (const k of keys) total += table[k];
    let r = this.next() * total;
    for (const k of keys) {
      r -= table[k];
      if (r < 0) return k;
    }
    return keys[keys.length - 1];
  }

  /** Index pick from parallel weights array. Returns -1 when all weights are 0. */
  weightedIndex(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) total += w;
    if (total <= 0) return -1;
    let r = this.next() * total;
    for (let i = 0; i < weights.length; i++) {
      r -= weights[i];
      if (r < 0) return i;
    }
    return weights.length - 1;
  }

  /** Approximately normal(0, 1) via sum of uniforms (cheap, deterministic). */
  normal(): number {
    return this.next() + this.next() + this.next() + this.next() - 2;
  }

  /** New uint32 seed, e.g. for PFP generation. */
  seed(): number {
    return Math.floor(this.next() * 4294967296) >>> 0;
  }
}

/** Create the initial stream state for a named system under a root seed. */
export function forkStream(rootSeed: number, name: string): RngStream {
  return { s: mixSeed(rootSeed >>> 0, hashString(name)) };
}

/**
 * Get (lazily creating) the named stream stored in a stream table and wrap it.
 * Adding a new system creates a new stream and never shifts other streams.
 */
export function streamRng(table: Record<string, RngStream>, rootSeed: number, name: string): Rng {
  if (!table[name]) table[name] = forkStream(rootSeed, name);
  return new Rng(table[name]);
}
